import { MessageType } from "../../../types/conversationInbox";

import type {
  InboxV2Account,
  InboxV2Conversation,
  InboxV2MessageType,
} from "../../types";
import {
  mapInboxV2TaggedSubtypeToMessageType,
  resolveInboxV2TaggedConversationSubtype,
} from "../../ui-meta/conversation-meta";
import { formatInboxV2Timestamp } from "../../utils/date-format";
import type {
  InboxV2RawConversation,
  InboxV2RawMessage,
} from "../real-api/types";

import { mapInboxV2Identity } from "./interlocutor";
import {
  mapLastMessageToInboxV2Message,
  mapMessageToInboxV2Message,
  mapSentimentTypeToInboxV2Sentiment,
  shouldStoreInboxV2Message,
} from "./message";
import { buildSourceContext } from "./post";

export function mapConversationToInboxV2Conversation(
  conversation: InboxV2RawConversation,
  options?: {
    account?: InboxV2Account;
    messages?: InboxV2RawMessage[];
    messagesLoaded?: boolean;
  }
): InboxV2Conversation {
  const sourceMessages =
    options?.messages ?? conversation.messages ?? conversation.items ?? [];
  const hasSourceMessages =
    options?.messages !== undefined ||
    conversation.messages !== undefined ||
    conversation.items !== undefined;
  const messages = sourceMessages
    .filter((message) => shouldStoreInboxV2Message(message))
    .map((element) => ({
      ...mapMessageToInboxV2Message(element, {
        account: options?.account,
        conversationAccountId: conversation.account,
      }),
      conversationId: conversation.id,
    }));
  const lastMessage =
    messages[messages.length - 1] ??
    mapLastMessageToInboxV2Message(conversation.lastMessage, conversation.id, {
      account: options?.account,
      conversationAccountId: conversation.account,
    });
  const messageType = mapConversationType(conversation);
  const conversationOwner = mapInboxV2Identity(conversation.interlocutor, {
    account: options?.account,
  });
  const customerIsUnknownSource =
    conversationOwner.isUnknownSource ||
    messages.some(
      (message) =>
        !message.isConversationAccountSender && message.authorIsUnknownSource
    ) ||
    Boolean(
      lastMessage &&
      !lastMessage.isConversationAccountSender &&
      lastMessage.authorIsUnknownSource
    );

  return {
    id: conversation.id,
    draftMessages: conversation.draftMessages,
    sourceConversationType: conversation.type,
    adType: conversation.adType,
    unreadCount: conversation.unreadCount ?? 0,
    accountId: conversation.account,
    platform: conversationOwner.platform,
    customerId: conversationOwner.id,
    customerPlatformId: conversationOwner.platformId,
    customerFetchedError: conversationOwner.fetchedError,
    customerIsUnknownSource,
    rootCommentPlatformId: getRootCommentPlatformId(sourceMessages),
    participantIds: conversationOwner.id ? [conversationOwner.id] : [],
    customerName: conversationOwner.name,
    customerHandle: conversationOwner.handle,
    customerAvatarUrl: conversationOwner.avatarUrl ?? "",
    lastMessageAt:
      lastMessage?.createdAt ??
      conversation.updatedAt?.toString() ??
      conversation.createdAt,
    lastActivityLabel: formatInboxV2Timestamp(
      lastMessage?.createdAt ?? conversation.updatedAt ?? conversation.createdAt
    ),
    updatedAt: conversation.updatedAt?.toString() ?? conversation.createdAt,
    sourceLabel: getSourceLabel(messageType),
    messageType,
    status: mapConversationStatus(conversation),
    statusRevision: conversation.statusRevision ?? 0,
    sentimentType: conversation.sentimentType
      ? mapSentimentTypeToInboxV2Sentiment(conversation.sentimentType)
      : undefined,
    assigneeId: conversation.assignedTo || undefined,
    tagIds: (conversation.tags ?? [])
      .filter((tag) => !tag.archived && !tag.hidden)
      .map((tag) => tag.id),
    indicators: buildIndicators(conversation),
    sourceContext: buildSourceContext(
      conversation,
      messageType,
      options?.account
    ),
    messages,
    messagesLoaded:
      options?.messagesLoaded ?? (hasSourceMessages && messages.length > 0),
    messagesNextToken: conversation.next ?? undefined,
    hasMoreMessages: Boolean(conversation.hasNext),
  };
}

export function mergeRealtimeConversationIntoInboxV2Conversation(
  conversation: InboxV2Conversation,
  realtimeConversation: Partial<InboxV2RawConversation>
): InboxV2Conversation {
  const nextConversation = { ...conversation };
  if ("draftMessages" in realtimeConversation) {
    nextConversation.draftMessages = realtimeConversation.draftMessages;
  }

  if (typeof realtimeConversation.unreadCount === "number") {
    nextConversation.unreadCount = realtimeConversation.unreadCount;
  }

  if (
    realtimeConversation.status &&
    (realtimeConversation.statusRevision ?? 0) >
      (conversation.statusRevision ?? -1)
  ) {
    nextConversation.statusRevision = realtimeConversation.statusRevision ?? 0;
    nextConversation.status = mapConversationStatus(
      realtimeConversation as InboxV2RawConversation
    );
  }

  if ("assignedTo" in realtimeConversation) {
    nextConversation.assigneeId = realtimeConversation.assignedTo || undefined;
  }

  if (realtimeConversation.type) {
    nextConversation.sourceConversationType = realtimeConversation.type;
    nextConversation.messageType = mapConversationType(
      realtimeConversation as InboxV2RawConversation
    );
    nextConversation.sourceLabel = getSourceLabel(nextConversation.messageType);
  }

  if (typeof realtimeConversation.sentimentType === "number") {
    nextConversation.sentimentType = mapSentimentTypeToInboxV2Sentiment(
      realtimeConversation.sentimentType
    );
  }

  if (realtimeConversation.updatedAt != null) {
    nextConversation.updatedAt = String(realtimeConversation.updatedAt);
  }

  nextConversation.indicators = mergeRealtimeConversationIndicators(
    conversation.indicators,
    realtimeConversation
  );

  return nextConversation;
}

function mergeRealtimeConversationIndicators(
  currentIndicators: InboxV2Conversation["indicators"],
  realtimeConversation: Partial<InboxV2RawConversation>
) {
  const indicators = new Set(currentIndicators);

  if (typeof realtimeConversation.isStarred === "boolean") {
    updateIndicator(indicators, "bookmark", realtimeConversation.isStarred);
  }

  if (typeof realtimeConversation.isMentioned === "boolean") {
    updateIndicator(indicators, "mention", realtimeConversation.isMentioned);
  }

  if ("adType" in realtimeConversation) {
    updateIndicator(indicators, "ad", Boolean(realtimeConversation.adType));
  }

  return [...indicators];
}

function updateIndicator(
  indicators: Set<InboxV2Conversation["indicators"][number]>,
  indicator: InboxV2Conversation["indicators"][number],
  isEnabled: boolean
) {
  if (isEnabled) {
    indicators.add(indicator);
  } else {
    indicators.delete(indicator);
  }
}

function getRootCommentPlatformId(sourceMessages: InboxV2RawMessage[]) {
  const rootComment = sourceMessages.find(
    (message) =>
      message.type === MessageType.ttThreadedComment &&
      "level" in message &&
      message.level === 0 &&
      typeof message.platformId === "string" &&
      message.platformId.trim().length > 0
  );

  return rootComment?.platformId ?? undefined;
}

function mapConversationStatus(
  conversation: InboxV2RawConversation
): InboxV2Conversation["status"] {
  return conversation.status === "CLOSED" ? "closed" : "open";
}

function mapConversationType(
  conversation: InboxV2RawConversation
): Exclude<InboxV2MessageType, "all"> {
  const taggedSubtype = resolveInboxV2TaggedConversationSubtype(conversation);
  if (taggedSubtype) {
    return mapInboxV2TaggedSubtypeToMessageType(taggedSubtype);
  }

  switch (conversation.type) {
    case "MESSAGE": {
      return "dm";
    }
    case "COMMENT": {
      return "comment";
    }
    case "THREADED_COMMENT": {
      return "threaded-comment";
    }
    case "POST": {
      return "post";
    }
    case "TAGGED_POST": {
      return "tagged-post";
    }
    case "REVIEW": {
      return "review";
    }
    case "QA": {
      return "qa";
    }
    default: {
      return "tagged-post";
    }
  }
}

function buildIndicators(
  conversation: InboxV2RawConversation
): InboxV2Conversation["indicators"] {
  const indicators: InboxV2Conversation["indicators"] = [];

  if (conversation.isStarred) {
    indicators.push("bookmark");
  }

  if (conversation.isMentioned) {
    indicators.push("mention");
  }

  if (conversation.adType?.trim()) {
    indicators.push("ad");
  }

  return indicators;
}

function getSourceLabel(messageType: Exclude<InboxV2MessageType, "all">) {
  switch (messageType) {
    case "dm": {
      return "Direct Message";
    }
    case "review": {
      return "Review";
    }
    case "qa": {
      return "Question";
    }
    case "post": {
      return "Post";
    }
    case "mention":
    case "mention-in-caption":
    case "tagged-post":
    case "tagged-in-media": {
      return "Tagged";
    }
    case "comment":
    case "threaded-comment":
    default: {
      return "Comment";
    }
  }
}
